import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, of } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  DatDevice,
  DatSegmentDetail,
  HistoryRange,
  Station,
  StationHistorySlot,
  TimeWindow,
} from '../models/monitoring.models';
import { buildDatWindow } from '../utils/time-utils';
import { HistoryDay } from '../utils/history-ranges';

@Injectable({ providedIn: 'root' })
export class MonitoringService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.apiBaseUrl;

  getStations(): Observable<Station[]> {
    return this.http.get<Station[]>(`${this.baseUrl}/stations`).pipe(catchError(() => of([])));
  }

  searchStations(query: string): Observable<Station[]> {
    let params = new HttpParams();
    const trimmed = query.trim();
    if (trimmed) {
      params = params.set('search', trimmed);
    }
    return this.http.get<Station[]>(`${this.baseUrl}/stations`, { params }).pipe(catchError(() => of([])));
  }

  getStation(id: string): Observable<Station | undefined> {
    return this.http.get<Station>(`${this.baseUrl}/stations/${encodeURIComponent(id)}`).pipe(
      catchError(() => of(undefined)),
    );
  }

  getHistoryRanges(stationId: string, day: HistoryDay = 'today'): Observable<HistoryRange[]> {
    const params = new HttpParams().set('day', day);
    return this.http
      .get<HistoryRange[]>(`${this.baseUrl}/stations/${encodeURIComponent(stationId)}/history-ranges`, { params })
      .pipe(catchError(() => of([])));
  }

  getStationHistory(
    stationId: string,
    range: Pick<HistoryRange, 'label' | 'startMinutes' | 'endMinutes'>,
    day: HistoryDay = 'today',
  ): Observable<StationHistorySlot[]> {
    const params = new HttpParams()
      .set('day', day)
      .set('rangeLabel', range.label)
      .set('rangeStart', String(range.startMinutes))
      .set('rangeEnd', String(range.endMinutes));
    return this.http
      .get<StationHistorySlot[]>(`${this.baseUrl}/stations/${encodeURIComponent(stationId)}/history`, { params })
      .pipe(catchError(() => of([])));
  }

  getDatDevices(stationId: string, time?: number, day: HistoryDay = 'today'): Observable<DatDevice[]> {
    let params = new HttpParams().set('day', day);
    if (time != null) {
      params = params.set('time', String(time));
    }
    return this.http
      .get<DatDevice[]>(`${this.baseUrl}/stations/${encodeURIComponent(stationId)}/dats`, { params })
      .pipe(catchError(() => of([])));
  }

  getDatDetail(
    stationId: string,
    datId: string,
    segmentStart: number,
    day: HistoryDay = 'today',
  ): Observable<DatSegmentDetail | undefined> {
    const params = new HttpParams().set('start', String(segmentStart)).set('day', day);
    return this.http
      .get<DatSegmentDetail>(
        `${this.baseUrl}/stations/${encodeURIComponent(stationId)}/dats/${encodeURIComponent(datId)}/detail`,
        { params },
      )
      .pipe(catchError(() => of(undefined)));
  }

  getDatWindow(centerMinutes: number): TimeWindow {
    return buildDatWindow(centerMinutes);
  }
}
